import * as SentryReact from '@sentry/react';
import axios, { AxiosError, isCancel } from 'axios';
import axiosRetry from 'axios-retry';

import { queryParamsModule } from '@/modules/queryParams';
import { objectToFormData } from '@/modules/utils';
import { webCampaign } from '@/modules/webCampaign';

import {
  IGNORED_AXIOS_ERROR_CODES,
  type IgnoredAxiosErrorCode,
} from './errorCodes';

axiosRetry(axios, { retries: 3 });

function shouldReportToSentry(error: AxiosError): boolean {
  const code = error.code;

  if (
    code &&
    IGNORED_AXIOS_ERROR_CODES.includes(code as IgnoredAxiosErrorCode)
  ) {
    return false;
  }

  return !isCancel(error);
}

axios.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (shouldReportToSentry(error)) {
      SentryReact.captureException(error, {
        tags: {
          errorType: 'axios',
          errorCode: error.code,
          httpStatus: error.response?.status?.toString(),
          httpMethod: error.config?.method?.toUpperCase(),
        },
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status: error.response?.status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          requestData: error.config?.data,
        },
      });
    }

    return Promise.reject(error);
  },
);

type ApiVersionType = 'v1' | 'v2' | 'base';

export interface APIOptionsType {
  errors?: boolean;
  multipart?: boolean;
  type?: string;
  withCredentials?: boolean;
}

const ENDPOINTS = {
  get v1() {
    if (queryParamsModule.getParam('env') === 'local') {
      return 'http://localhost:8000';
    }
    return import.meta.env.VITE_API_V1_URL;
  },
  get v2() {
    return import.meta.env.VITE_API_V2_URL;
  },
  get base() {
    return import.meta.env.VITE_API_BASE_URL;
  },
};

const BUNDLE_ID = import.meta.env.VITE_APP_BUNDLE_ID;
const LURE_APP_BUNDLE_ID = import.meta.env.VITE_LURE_APP_BUNDLE_ID;

class API {
  private token: string | undefined = '';

  authorize(token: string): void {
    this.token = token;
  }

  request<DType>(
    method: string,
    version: ApiVersionType,
    route: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any = {},
    options: APIOptionsType = {
      errors: false,
      multipart: false,
    },
  ): Promise<DType> {
    const url = [ENDPOINTS[version], route].join('');
    let params;
    const registrationPlatform = webCampaign.getRegistrationPlatform();
    const appSpec =
      registrationPlatform === 'web_lure' ? 'web_lure/1.0.0' : 'web/1.0.0';
    const bundleId =
      registrationPlatform === 'web_lure' ? LURE_APP_BUNDLE_ID : BUNDLE_ID;
    let headers = {
      'X-Bundle-ID': bundleId,
      'X-App-Spec': appSpec,
    };

    Object.keys(data).forEach((k) => !data[k] && delete data[k]);

    if (this.token) {
      headers = { ...headers, ...{ Authorization: `Bearer ${this.token}` } };
    }

    if (options.multipart) {
      data = objectToFormData(data);
    } else {
      headers = { ...headers, ...{ 'Content-Type': 'application/json' } };
    }

    if (method === 'GET') {
      params = data;
      data = {};
    }

    const response = axios
      .request<DType>({
        url,
        method,
        params,
        headers,
        data,
        withCredentials: options.withCredentials,
      })
      .then((_response) => _response.data);

    if (!options.errors) {
      response.catch(() => {});
    }

    return response;
  }

  get<DType>(
    apiVersion: ApiVersionType,
    route: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any,
    options?: APIOptionsType,
  ): Promise<DType> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.request<DType>('GET', apiVersion, route, data, options);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  post<DType, ReqType = any>(
    apiVersion: ApiVersionType,
    route: string,
    data?: ReqType,
    options?: APIOptionsType,
  ): Promise<DType> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.request<DType>('POST', apiVersion, route, data, options);
  }

  patch<DType>(
    apiVersion: ApiVersionType,
    route: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any,
    options?: APIOptionsType,
  ): Promise<DType> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.request<DType>('PATCH', apiVersion, route, data, options);
  }

  put<DType>(
    apiVersion: ApiVersionType,
    route: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any,
    options?: APIOptionsType,
  ): Promise<DType> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.request<DType>('PUT', apiVersion, route, data, options);
  }

  delete<DType>(
    apiVersion: ApiVersionType,
    route: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any,
    options?: APIOptionsType,
  ): Promise<DType> {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.request<DType>('DELETE', apiVersion, route, data, options);
  }
}

export const apiInstance = axios;

export default new API();
